import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import superagent from 'superagent';
import { toast } from 'react-toastify';

import { Row } from '@shadcn/components/ui/row';
import { Col } from '@shadcn/components/ui/col';
import { Card, CardContent as CardBody, CardHeader } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { backendUrl } from '../../../helpers/consts.js';
import useQuery from '../../../helpers/JIFI/useQuery';

const ACTIVE_STATUSES = ['pending', 'processing'];
const POLL_INTERVAL_MS = 5000;

const STATUS_BADGE = {
  pending: 'warning',
  processing: 'info',
  ready: 'success',
  error: 'danger',
  cancelled: 'secondary',
  expired: 'dark',
};

const TYPE_LABEL = {
  orders: 'Orders',
};

const formatFilterSummary = (filters) => {
  if (!filters) return '—';
  const parts = [];
  if (filters.filterStartDate || filters.filterEndDate) {
    parts.push(`${filters.filterStartDate ?? '…'} → ${filters.filterEndDate ?? '…'}`);
  }
  if (filters.filterRegion) parts.push(`region: ${filters.filterRegion}`);
  if (filters.filterStatus) parts.push(`status: ${filters.filterStatus}`);
  if (filters.filterShipping) parts.push(`shipping: ${filters.filterShipping}`);
  if (filters.searchTerm) parts.push(`"${filters.searchTerm}"`);
  return parts.length ? parts.join(' · ') : 'no filters';
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Downloads = () => {
  const [refresh, setRefresh] = useState(0);
  const [data, state] = useQuery({
    url: '/orders/exports',
    queryData: {},
    refresh,
  });

  const exports = useMemo(() => {
    const rows = data?.data ?? [];
    return [...rows].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [data]);

  const hasActive = useMemo(
    () => exports.some((e) => ACTIVE_STATUSES.includes(e.status)),
    [exports],
  );

  useEffect(() => {
    if (!hasActive) return;
    const id = setInterval(() => setRefresh((n) => n + 1), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasActive]);

  const handleDownload = async (id) => {
    try {
      const response = await superagent
        .get(`${backendUrl}/orders/exports/${id}/download`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .set('accept', 'json');
      const url = response.body?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Could not get download URL');
      }
    } catch (err) {
      toast.error(err?.message ?? 'Download failed');
    }
  };

  const handleCancel = async (id) => {
    try {
      await superagent
        .delete(`${backendUrl}/orders/exports/${id}`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`);
      toast.success('Export cancelled');
      setRefresh((n) => n + 1);
    } catch (err) {
      toast.error(err?.response?.body?.message ?? err?.message ?? 'Cancel failed');
    }
  };

  const cellStyle = { padding: '16px 12px', verticalAlign: 'middle' };

  return (
    <>
      <div className="page-content">
        <div className="w-full px-3 mx-auto xl:max-w-[85%] mb-6">
          <Breadcrumbs title="Orders" breadcrumbItem="Downloads" />
          <Row>
            <Col className="w-full">
              <Card className="mb-6">
                <CardBody>
                  {state === 'loading' && exports.length === 0 && (
                    <p className="text-muted mb-0">Loading…</p>
                  )}
                  {state === 'success' && exports.length === 0 && (
                    <p className="text-muted mb-0">
                      No exports yet. Trigger one from the Orders page.
                    </p>
                  )}

                  {exports.length > 0 && (
                    <table className="table mb-0">
                      <thead>
                        <tr>
                          <th style={{ ...cellStyle, fontWeight: 600 }}>Created</th>
                          <th style={{ ...cellStyle, fontWeight: 600 }}>Source</th>
                          <th style={{ ...cellStyle, fontWeight: 600 }}>Filters</th>
                          <th style={{ ...cellStyle, fontWeight: 600 }}>Rows</th>
                          <th style={{ ...cellStyle, fontWeight: 600 }}>Status</th>
                          <th style={{ ...cellStyle, fontWeight: 600, textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exports.map((row) => {
                          const badgeClass = `badge badge-soft-${STATUS_BADGE[row.status] ?? 'secondary'}`;
                          return (
                            <tr key={row._id}>
                              <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                                <div>{moment(row.createdAt).format('YYYY-MM-DD HH:mm')}</div>
                                <small style={{ color: '#64748b' }}>{moment(row.createdAt).fromNow()}</small>
                              </td>
                              <td style={cellStyle}>
                                <span className="badge badge-soft-info" style={{ fontSize: 12, padding: '4px 10px' }}>
                                  {TYPE_LABEL[row.type] ?? row.type ?? 'Orders'}
                                </span>
                              </td>
                              <td style={{ ...cellStyle, color: '#6c757d' }}>
                                {formatFilterSummary(row.filters)}
                              </td>
                              <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                                <div>{row.rowCount?.toLocaleString() ?? '—'}</div>
                                {row.fileSizeBytes && (
                                  <small style={{ color: '#64748b' }}>{formatFileSize(row.fileSizeBytes)}</small>
                                )}
                              </td>
                              <td style={cellStyle}>
                                <span className={badgeClass} style={{ fontSize: 12, padding: '4px 10px', textTransform: 'uppercase' }}>
                                  {row.status}
                                </span>
                              </td>
                              <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {row.status === 'ready' && (
                                  <Button size="sm" variant="primary" onClick={() => handleDownload(row._id)}>
                                    <i className="bx bx-download mr-1"></i>
                                    Download
                                  </Button>
                                )}
                                {ACTIVE_STATUSES.includes(row.status) && (
                                  <Button size="sm" variant="outline" onClick={() => handleCancel(row._id)}>
                                    Cancel
                                  </Button>
                                )}
                                {row.status === 'error' && row.errorMessage && (
                                  <span
                                    className="text-danger"
                                    style={{ fontSize: 12 }}
                                    title={row.errorMessage}
                                  >
                                    {row.errorMessage.slice(0, 60)}
                                    {row.errorMessage.length > 60 ? '…' : ''}
                                  </span>
                                )}
                                {row.status === 'expired' && (
                                  <small className="text-muted">file deleted</small>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default Downloads;
